import { createReducer, on } from '@ngrx/store';
import { reflectionActions } from '../actions/reflection.actions';
import { Reflection } from '../models';

interface ReflectionEntry {
    loaded: boolean | null;
    loading: boolean;
    reflection: Reflection | null;
    error: number | null;
}

export interface ReflectionState {
    saving: boolean;
    saved: boolean;
    createdId: string | null;
    entries: Record<string, ReflectionEntry>;
}

export const initialState: ReflectionState = {
    saving: false,
    saved: false,
    createdId: null,
    entries: {},
};

export const reflectionReducer = createReducer(
    initialState,

    // Get
    on(reflectionActions.getReflection, (state, { id }): ReflectionState => ({
        ...state,
        entries: {
            ...state.entries,
            [id]: { loaded: null, loading: true, reflection: null, error: null },
        },
    })),
    on(reflectionActions.getReflectionSuccess, (state, { payload }): ReflectionState => ({
        ...state,
        entries: {
            ...state.entries,
            [payload.id]: { loaded: true, loading: false, reflection: payload, error: null },
        },
    })),
    on(reflectionActions.getReflectionError, (state, { id, code }): ReflectionState => ({
        ...state,
        entries: {
            ...state.entries,
            [id]: { loaded: false, loading: false, reflection: null, error: code },
        },
    })),

    // Create
    on(reflectionActions.createReflection, (state): ReflectionState => ({
        ...state,
        saving: true,
        saved: false,
    })),
    on(reflectionActions.createReflectionSuccess, (state, { payload }): ReflectionState => ({
        ...state,
        saving: false,
        saved: true,
        createdId: payload.id,
        entries: {
            ...state.entries,
            [payload.id]: { loaded: true, loading: false, reflection: payload, error: null },
        },
    })),
    on(reflectionActions.createReflectionError, (state): ReflectionState => ({
        ...state,
        saving: false,
        saved: false,
    })),

    // Update
    on(reflectionActions.updateReflection, (state): ReflectionState => ({
        ...state,
        saving: true,
        saved: false,
    })),
    on(reflectionActions.updateReflectionSuccess, (state, { payload }): ReflectionState => ({
        ...state,
        saving: false,
        saved: true,
        entries: {
            ...state.entries,
            [payload.id]: { loaded: true, loading: false, reflection: payload, error: null },
        },
    })),
    on(reflectionActions.updateReflectionError, (state): ReflectionState => ({
        ...state,
        saving: false,
        saved: false,
    })),

    // Delete
    on(reflectionActions.deleteReflection, (state): ReflectionState => ({
        ...state,
        saving: true,
        saved: false,
    })),
    on(reflectionActions.deleteReflectionSuccess, (state, { id }): ReflectionState => {
        const reflectionEntries = { ...state.entries };
        delete reflectionEntries[id];

        return {
            ...state,
            saving: false,
            saved: true,
            entries: reflectionEntries,
        };
    }),
    on(reflectionActions.deleteReflectionError, (state): ReflectionState => ({
        ...state,
        saving: false,
        saved: false,
    })),

    // Clear
    on(reflectionActions.clear, (): ReflectionState => ({ ...initialState })),
);
